/**
 * The Findings triage queue (pillar A): views rail, a dense prioritised
 * queue served page by page by `finding.queue`, a detail pane (Enter),
 * single-key triage, bulk actions (incl. "select all matching filter")
 * and undo. Everything the keys do is also a button.
 */
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { Selection } from "react-aria-components";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Copy,
  Inbox,
  Info,
  MoreHorizontal,
  Route,
  Link2,
  Maximize2,
} from "lucide-react";
import {
  ZenFindingsCarousel,
  TargetCropThumbnail,
  type ZenFindingItem,
} from "@/components/walkthrough/zen-mode";
import type { TargetBox } from "@/components/walkthrough/step-screenshot";
import { toast } from "sonner";
import {
  type FindingQueueQuery,
  type QueueSystemView,
  isProblemType,
} from "@iamu/shared";
import {
  BatchActionBar,
  BatchActionButton,
} from "@/components/batch-action-bar";
import { EmptyState } from "@/components/empty-state";
import {
  BatchExportButton,
  FindingFixDropdown,
  FindingTriageMenu,
} from "@/components/experience/finding-actions";
import { FindingRow } from "@/components/experience/finding-row";
import { BulkCreateIssuesDialog } from "@/components/workflow/bulk-create-issues-dialog";
import {
  CreateIssueDialog,
  LinkExistingDialog,
} from "@/components/workflow/create-issue-dialog";
import { ProviderIcon } from "@/components/workflow/workflow-rail";
import {
  type TriageAction,
  TriageActionDialog,
} from "@/components/experience/triage-action-dialog";
import { useTransitionFindings } from "@/components/experience/use-transition-findings";
import { useWorkRequests } from "@/components/experience/use-work-requests";
import { WorkRequestChip } from "@/components/experience/work-request-chip";
import { SupersedeRequestDialog } from "@/components/experience/supersede-request-dialog";
import type { FindingType, Severity } from "@/components/experience/vocabulary";
import {
  AriaGridList,
  AriaGridListCheckbox,
  AriaGridListItem,
} from "@/components/ui/aria-gridlist";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { STALE_TIMES } from "@/lib/query-performance";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { useProjectRole } from "@/lib/use-project-role";
import { useFirstRunCopy } from "@/lib/project-access";
import { projectExportConfigSchema } from "@iamu/shared";
import { KeyboardHelpDialog } from "./keyboard-help-dialog";
import { OverridePriorityDialog } from "./override-priority-dialog";
import { PriorityChip } from "./priority-chip";
import { QueueDetailPane } from "./queue-detail-pane";
import { PANE_BUTTONS, PaneActions } from "./pane-actions";
import { ScopeEmptyState } from "@/components/findings-scope/scope-empty-state";
import { hasBuildScope } from "@/components/findings-scope/scope-url-state";
import { QueueFilters } from "./queue-filters";
import {
  commandForKey,
  isTypingTarget,
  type QueueCommand,
  rangeBetween,
} from "./queue-keys";
import { QueueRail, SYSTEM_VIEW_LABEL } from "./queue-rail";
import type { QueueGroupData, QueueRowData } from "./queue-types";
import {
  effectiveQuery,
  parseQueueUrl,
  queueUrlParams,
} from "./queue-url-state";

const PAGE_SIZE = 50;
const BULK_CAP = 1000;

interface DialogState {
  action: TriageAction;
  ids: string[];
  query?: FindingQueueQuery;
  subject: string;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * "Fix this" (fix prompt / issue) is offered while a problem is open work —
 * the open states of the lifecycle (`OPEN_STATES` in @iamu/db).
 */
const FIXABLE_STATES = new Set(["new", "open", "in_progress", "regressed"]);

/**
 * Column labels for the desktop row layout. Below `md` rows stack (title, then
 * meta, then actions), so the fixed-width labels would only clip: hide them.
 */
function QueueColumnHeader() {
  return (
    <div className="hidden items-center justify-between border-b border-border/80 bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground md:flex">
      <div className="flex items-center gap-3">
        <span className="w-4" />
        <span>Finding</span>
      </div>
      <div className="flex items-center gap-4 text-right">
        <span className="w-16 text-right">Last seen</span>
        <span className="w-48 text-right">Actions</span>
      </div>
    </div>
  );
}

export function FindingsQueue({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const url = useMemo(() => parseQueueUrl(params), [params]);
  const { canEdit } = useProjectRole(projectId);
  const requests = useWorkRequests(projectId);

  const { data: views } = trpc.findingView.list.useQuery({ projectId });
  const saved = url.savedViewId
    ? (views?.find((v) => v.id === url.savedViewId) ?? null)
    : null;
  const query = effectiveQuery(url, saved?.query ?? null);

  const queue = trpc.finding.queue.useInfiniteQuery(
    { projectId, query, limit: PAGE_SIZE },
    {
      getNextPageParam: (last) => last.nextCursor ?? undefined,
      staleTime: STALE_TIMES.list,
    },
  );
  const first = queue.data?.pages[0];
  const lastCountsRef = useRef(first?.counts);
  if (first?.counts) {
    lastCountsRef.current = first.counts;
  }
  const rows = useMemo(
    () =>
      (queue.data?.pages.flatMap((p) => p.rows) ??
        []) as unknown as QueueRowData[],
    [queue.data],
  );
  const ids = useMemo(() => rows.map((r) => r.id), [rows]);
  const counts = first?.counts ?? lastCountsRef.current;
  const isFiltered = Boolean(
    query.search ||
      query.severities ||
      query.personaId ||
      query.capabilityId ||
      query.judgeCited ||
      (query.type && query.type !== "problems"),
  );
  const knownViewCount =
    !isFiltered && url.view && counts?.[url.view] !== undefined
      ? counts[url.view]
      : undefined;
  const total = first?.total ?? knownViewCount ?? 0;
  const groups = (first?.groups ?? null) as QueueGroupData[] | null;
  const viewLabel = url.view
    ? SYSTEM_VIEW_LABEL[url.view]
    : (saved?.name ?? "this view");

  const { data: members } = trpc.project.members.useQuery({ projectId });
  const [bulkIssues, setBulkIssues] = useState(false);
  const [createIssueCanonicalId, setCreateIssueCanonicalId] = useState<
    string | null
  >(null);
  const [linkExistingCanonicalId, setLinkExistingCanonicalId] = useState<
    string | null
  >(null);
  const { data: projectData } = trpc.project.getById.useQuery({
    id: projectId,
  });
  const { data: projectContext } = trpc.project.getContext.useQuery({
    projectId,
  });

  // ── Focus, selection, dialogs ────────────────────────────────────
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [allMatching, setAllMatching] = useState(false);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [overrideRow, setOverrideRow] = useState<QueueRowData | null>(null);
  const [whyOpenId, setWhyOpenId] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [zenOpen, setZenOpen] = useState(false);
  const [zenIndex, setZenIndex] = useState(0);
  const zenOpenRef = useRef(zenOpen);
  zenOpenRef.current = zenOpen;

  const zenFindings = useMemo((): ZenFindingItem[] => {
    return rows.map((r) => ({
      id: r.id,
      title: r.title,
      description: r.preview || "",
      type: (r.type ?? "hinders") as FindingType,
      severity: (r.severity ?? null) as Severity | null,
      personaName: r.personaName,
      walkthroughId: r.walkthroughId,
      stepNumber: r.stepNumber,
      targetBox: r.targetBox as TargetBox | null,
      pageUrl: r.pageUrl,
      breaksRule: r.breaksRule,
      breaksFile: r.breaksFile,
      screen: r.screen,
      lens: r.lens,
      canonicalFindingId: r.id,
      triageStatus: r.state,
    }));
  }, [rows]);

  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const moveFocus = useRef(false);
  const pendingG = useRef(false);
  const transition = useTransitionFindings(projectId);
  const utils = trpc.useUtils();
  const recheckMutation = trpc.finding.recheckCleanRuns.useMutation();
  const bulkAssignMutation = trpc.workflow.assign.useMutation({
    onSuccess: () => {
      toast.success("Assigned findings");
      utils.finding.queue.invalidate();
      setSelected(new Set());
    },
    onError: (err) => toastTrpcError(err, "Failed to assign findings"),
  });

  const queryKey = JSON.stringify(query);
  // A new view or filter starts a fresh selection.
  useEffect(() => {
    setSelected(new Set());
    setAllMatching(false);
    setAnchorId(null);
  }, [queryKey]);

  // When no view was explicitly set in the URL (or ?view=queue was passed),
  // and fix_next has 0 problems while triage has items, default into the triage inbox.
  useEffect(() => {
    const rawView = params.get("view");
    const hasExplicitView = Boolean(rawView && rawView !== "queue");
    if (
      !hasExplicitView &&
      !url.savedViewId &&
      !url.query.periodBucket &&
      counts &&
      counts.fix_next === 0 &&
      (counts.triage ?? 0) > 0
    ) {
      setParams(queueUrlParams({ ...url, view: "triage" }), { replace: true });
    }
  }, [counts, params, url, setParams]);

  // Keep a valid focused row.
  useEffect(() => {
    if (rows.length === 0) {
      if (focusedId) setFocusedId(null);
      return;
    }
    if (!focusedId || !ids.includes(focusedId)) {
      setFocusedId(
        url.focusId && ids.includes(url.focusId) ? url.focusId : ids[0],
      );
    }
  }, [ids, rows.length, focusedId, url.focusId]);

  // Move DOM focus when a key (not the mouse) moved the focused row.
  useEffect(() => {
    if (!moveFocus.current || !focusedId) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-key="${CSS.escape(focusedId)}"]`,
    );
    if (el) {
      el.focus();
      el.scrollIntoView({ block: "nearest" });
      moveFocus.current = false;
    }
  }, [focusedId, rows]);

  const setUrl = useCallback(
    (patch: Partial<typeof url>) => {
      setParams(queueUrlParams({ ...url, ...patch }), { replace: true });
    },
    [url, setParams],
  );
  const openPane = (id: string | null) => setUrl({ focusId: id });
  const paneRow = url.focusId
    ? (rows.find((r) => r.id === url.focusId) ?? null)
    : null;

  /** Ids a key or button acts on: the selection, else the focused row. */
  const targetIds = (): string[] =>
    selected.size > 0
      ? [...selected]
      : focusedId && ids.includes(focusedId)
        ? [focusedId]
        : [];
  const targetQuery = allMatching ? query : undefined;
  const subjectFor = (list: string[]) =>
    allMatching
      ? plural(total, "finding")
      : list.length === 1
        ? (rows.find((r) => r.id === list[0])?.title ?? "This finding")
        : plural(list.length, "finding");

  /** After a move: advance past the moved rows and announce what's left. */
  const afterMove = (moved: string[], message: string, changed: number) => {
    const left = Math.max(0, total - changed);
    setAnnouncement(
      `${message}. ${plural(left, "finding")} left in ${viewLabel}.`,
    );
    const stillHere = ids.filter((id) => !moved.includes(id));
    if (focusedId && moved.includes(focusedId)) {
      const idx = ids.indexOf(focusedId);
      const next =
        ids.slice(idx + 1).find((id) => !moved.includes(id)) ??
        stillHere[stillHere.length - 1] ??
        null;
      moveFocus.current = true;
      setFocusedId(next);
      if (url.focusId && moved.includes(url.focusId)) openPane(next);
    }
    setSelected(new Set());
    setAllMatching(false);
  };

  const runMove = (
    to: "open" | "fix_applied",
    message: string,
    list = targetIds(),
  ) => {
    if (!allMatching && list.length === 0) return;
    transition.run(
      allMatching ? { ids: [], query, projectId, to } : { ids: list, to },
      message,
      (res) => afterMove(allMatching ? ids : list, message, res.changed),
    );
  };

  const runDismiss = (list = targetIds()) => {
    if (!allMatching && list.length === 0) return;
    transition.run(
      allMatching
        ? {
            ids: [],
            query,
            projectId,
            to: "dismissed",
            reason: "not_relevant",
          }
        : { ids: list, to: "dismissed", reason: "not_relevant" },
      "Dismissed",
      (res) => afterMove(allMatching ? ids : list, "Dismissed", res.changed),
    );
  };

  const openDialog = (action: TriageAction, list = targetIds()) => {
    if (!allMatching && list.length === 0) return;
    setDialog({
      action,
      ids: list,
      query: targetQuery,
      subject: subjectFor(list),
    });
  };

  const goView = (view: QueueSystemView) =>
    navigate(`/projects/${projectId}/findings?view=${view}`);

  // ── Keyboard ────────────────────────────────────────────────────
  const runCommand = (command: QueueCommand) => {
    const focused = focusedId ? rows.find((r) => r.id === focusedId) : null;
    const step = (by: 1 | -1) => {
      if (ids.length === 0) return;
      const i = focusedId ? ids.indexOf(focusedId) : -1;
      const next = ids[Math.min(ids.length - 1, Math.max(0, i + by))];
      moveFocus.current = true;
      setFocusedId(next);
      if (url.focusId) openPane(next);
    };
    switch (command) {
      case "next":
        return step(1);
      case "prev":
        return step(-1);
      case "toggleSelect":
        if (!focusedId) return;
        setSelected((s) => {
          const n = new Set(s);
          n.has(focusedId) ? n.delete(focusedId) : n.add(focusedId);
          return n;
        });
        setAnchorId(focusedId);
        return;
      case "rangeSelect":
        if (!focusedId) return;
        setSelected(
          (s) => new Set([...s, ...rangeBetween(ids, anchorId, focusedId)]),
        );
        return;
      case "open":
        if (focusedId) openPane(focusedId);
        return;
      case "close":
        if (url.focusId) {
          moveFocus.current = true;
          openPane(null);
        } else {
          setSelected(new Set());
          setAllMatching(false);
        }
        return;
      case "accept":
        return runMove("open", "Accepted");
      case "createIssue": {
        const targetId = paneRow?.id ?? focusedId;
        if (targetId) {
          setCreateIssueCanonicalId(targetId);
        }
        return;
      }
      case "linkIssue": {
        const targetId = paneRow?.id ?? focusedId;
        if (targetId) {
          setLinkExistingCanonicalId(targetId);
        }
        return;
      }
      case "duplicate":
        return openDialog("duplicate");
      case "dismiss":
        return runDismiss();
      case "override":
        if (focused && focused.priorityScore !== null) setOverrideRow(focused);
        return;
      case "why":
        if (focusedId) setWhyOpenId(focusedId);
        return;
      case "undo":
        setAnnouncement(
          transition.undoLast()
            ? "Undoing the last triage."
            : "Nothing to undo.",
        );
        return;
      case "search":
        searchRef.current?.focus();
        return;
      case "help":
        setHelpOpen(true);
        return;
      case "goTriage":
        return goView("triage");
      case "goFixNext":
        return goView("fix_next");
      case "goAwaiting":
        return goView("awaiting_rerun");
    }
  };
  const commandRef = useRef(runCommand);
  commandRef.current = runCommand;
  const editRef = useRef(canEdit);
  editRef.current = canEdit;

  useEffect(() => {
    const WRITE: ReadonlySet<QueueCommand> = new Set([
      "accept",
      "createIssue",
      "duplicate",
      "dismiss",
      "override",
      "undo",
    ]);
    function onKey(e: KeyboardEvent) {
      if (e.defaultPrevented || isTypingTarget(e.target) || zenOpenRef.current)
        return;
      // Enter / Space on a button or link inside a row belong to it.
      const t = e.target as HTMLElement | null;
      if (
        (e.key === "Enter" || e.key === " ") &&
        t?.closest("button, a") &&
        !t?.matches('[role="row"]')
      ) {
        return;
      }
      const { command, pendingG: g } = commandForKey(e, pendingG.current);
      pendingG.current = g;
      if (!command) return;
      if (WRITE.has(command) && !editRef.current) {
        setAnnouncement("Viewers can't triage findings.");
        return;
      }
      e.preventDefault();
      commandRef.current(command);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ── Render ──────────────────────────────────────────────────────
  const selectedCount = allMatching ? total : selected.size;
  const selectionIds = [...selected];

  const rowActions = (row: QueueRowData): ReactNode => {
    const isNew = row.state === "new";
    const isDismissed = row.state === "dismissed";

    return (
      <>
        {isNew && row.cleanRerun && (
          <Button
            variant="default"
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
            disabled={recheckMutation.isPending}
            onClick={(e) => {
              e.stopPropagation();
              recheckMutation.mutate(
                { projectId },
                {
                  onSuccess: () => {
                    toast.success("Verified from clean re-run");
                    utils.finding.queue.invalidate();
                    utils.finding.queueCounts.invalidate();
                  },
                  onError: (err) =>
                    toastTrpcError(err, "Failed to verify from clean re-run"),
                },
              );
            }}
          >
            <CheckCircle2 className="size-4" />
            Verify
          </Button>
        )}
        {isNew && !row.cleanRerun && (
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              runMove("open", "Accepted", [row.id]);
            }}
          >
            <Check className="size-4" />
            Accept
          </Button>
        )}
        {isDismissed ? (
          <Button
            variant="outline"
            size="sm"
            disabled={transition.isPending}
            onClick={(e) => {
              e.stopPropagation();
              transition.run(
                { ids: [row.id], to: "open", reason: "reopened" },
                "Reopened",
                (res) => afterMove([row.id], "Reopened", res.changed),
              );
            }}
          >
            Reopen
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            disabled={transition.isPending}
            onClick={(e) => {
              e.stopPropagation();
              runDismiss([row.id]);
            }}
          >
            Dismiss
          </Button>
        )}
        <FindingTriageMenu
          findingId={row.id}
          projectId={projectId}
          state={row.state}
          findingType={row.type as FindingType | null}
          subject={row.title ?? undefined}
          hasLinkedRepo={!!projectData?.githubRepositoryId}
          includeFixActions
          requests={requests}
          open={requests.byCanonical.get(row.id) ?? null}
          iconOnly
        />
      </>
    );
  };

  const renderRow = (row: QueueRowData, headingLevel: "h2" | "h3") => (
    <AriaGridListItem
      key={row.id}
      id={row.id}
      textValue={row.title ?? row.preview ?? row.id}
      className={`items-stretch gap-3 py-2.5 px-3 transition-colors hover:bg-muted/40 ${
        row.id === url.focusId ? "bg-muted" : ""
      }`}
    >
      <AriaGridListCheckbox
        aria-label={`Select ${row.title ?? row.preview ?? "finding"}`}
      />
      <FindingRow
        headingLevel={headingLevel}
        onSelect={(id) => openPane(id)}
        finding={{
          id: row.id,
          title: row.title,
          description: row.preview ?? "",
          breaksRule: row.breaksRule,
          breaksFile: row.breaksFile,
          screen: row.screen,
          lens: row.lens,
          type: row.type as FindingType | null,
          severity: row.severity as Severity | null,
          state: row.state,
          stateReason: row.stateReason,
          observationCount: row.observationCount,
          periodObservationCount: row.periodObservationCount,
          personaName: row.personaName,
          scenarioName: row.outlineName,
          firstSeenAt: row.firstSeenAt,
          lastSeenAt: row.lastSeenAt,
          stepNumber: row.stepNumber,
          walkthroughId: row.walkthroughId,
          targetBox: row.targetBox as never,
        }}
        muted={row.state === "dismissed"}
        trailing={
          row.walkthroughId && typeof row.stepNumber === "number" ? (
            <div
              className="hidden lg:block h-12 w-20 shrink-0 overflow-hidden rounded border border-border/60 hover:border-border hover:shadow-xs transition-all cursor-zoom-in group/thumb"
              onClick={(e) => {
                e.stopPropagation();
                const idx = rows.findIndex((r) => r.id === row.id);
                setZenIndex(Math.max(0, idx));
                setZenOpen(true);
              }}
              title="Click to inspect in Zen Carousel"
            >
              <TargetCropThumbnail
                walkthroughId={row.walkthroughId}
                stepNumber={row.stepNumber}
                targetBox={row.targetBox as TargetBox}
                targetTone={row.severity || row.type}
                alt={`Step ${row.stepNumber} target preview`}
                className="h-full w-full"
              />
            </div>
          ) : undefined
        }
        priority={
          <PriorityChip
            canonicalId={row.id}
            priority={row}
            open={whyOpenId === row.id}
            onOpenChange={(o) => setWhyOpenId(o ? row.id : null)}
          />
        }
        meta={
          <>
            {requests.byCanonical.has(row.id) && (
              <WorkRequestChip
                request={requests.byCanonical.get(row.id)!}
                onCancel={
                  canEdit
                    ? () =>
                        requests.cancel(requests.byCanonical.get(row.id)!.id)
                    : undefined
                }
              />
            )}
            <RowMeta row={row} />
          </>
        }
        actions={canEdit ? rowActions(row) : undefined}
      />
    </AriaGridListItem>
  );

  const gridProps = {
    selectionMode: "multiple" as const,
    selectionBehavior: "toggle" as const,
    disallowTypeAhead: true,
    selectedKeys: selected as Selection,
    onSelectionChange: (keys: Selection) => {
      setAllMatching(false);
      setSelected(
        keys === "all" ? new Set(ids) : new Set([...keys].map(String)),
      );
    },
    onAction: (key: React.Key) => openPane(String(key)),
    className: "gap-0 divide-y divide-border/60",
  };

  const list = (() => {
    if (queue.isLoading) {
      return (
        <div className="space-y-3" aria-busy="true">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      );
    }
    if (queue.error) {
      return (
        <EmptyState
          icon={AlertCircle}
          title="Failed to load findings"
          description="Something went wrong while loading findings. Please try again."
          actions={
            <Button variant="outline" size="sm" onClick={() => queue.refetch()}>
              Try again
            </Button>
          }
          variant="compact"
        />
      );
    }
    if (rows.length === 0) {
      return (
        <QueueEmpty
          projectId={projectId}
          view={url.view}
          counts={counts}
          filtered={
            !!(
              query.search ||
              query.severities ||
              query.personaId ||
              query.capabilityId ||
              query.lens ||
              query.judgeCited
            )
          }
          scoped={Boolean(query.period) || hasBuildScope(query)}
        />
      );
    }
    if (groups && groups.length > 0) {
      return groups.map((g) => {
        const groupRows = rows.filter((r) => (r.groupKey ?? "") === g.key);
        if (groupRows.length === 0) return null;
        const label = groupLabel(query.group, g);
        return (
          <section
            key={g.key}
            aria-labelledby={`group-${g.key}`}
            className="space-y-2"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
              <h2 id={`group-${g.key}`} className="text-sm font-semibold">
                {label}{" "}
                <span className="text-xs font-normal tabular-nums text-muted-foreground">
                  {g.count}
                </span>
              </h2>
              {url.view === "awaiting_rerun" && g.scenarioId && (
                <RerunCommand scenarioId={g.scenarioId} />
              )}
            </div>
            <div className="rounded-lg border border-border/80 bg-card overflow-hidden shadow-xs dark:shadow-[inset_0_1px_0_0_oklch(1_0_0_/_0.06),0_6px_24px_-6px_oklch(0_0_0_/_0.4)]">
              <QueueColumnHeader />
              <AriaGridList aria-label={`Findings: ${label}`} {...gridProps}>
                {groupRows.map((r) => renderRow(r, "h3"))}
              </AriaGridList>
            </div>
          </section>
        );
      });
    }
    return (
      <div className="rounded-lg border border-border/80 bg-card overflow-hidden shadow-xs dark:shadow-[inset_0_1px_0_0_oklch(1_0_0_/_0.06),0_6px_24px_-6px_oklch(0_0_0_/_0.4)]">
        <QueueColumnHeader />
        <AriaGridList aria-label="Findings" {...gridProps}>
          {rows.map((r) => renderRow(r, "h2"))}
        </AriaGridList>
      </div>
    );
  })();

  const paneActions = paneRow ? (
    <PaneActions
      row={paneRow}
      canEdit={canEdit}
      projectId={projectId}
      hasLinkedRepo={!!projectData?.githubRepositoryId}
      requests={requests}
      members={members}
      onCommand={(c) => {
        setFocusedId(paneRow.id);
        setSelected(new Set());
        setAllMatching(false);
        // Run on the pane's finding regardless of the list selection.
        if (c === "accept") return runMove("open", "Accepted", [paneRow.id]);
        if (c === "createIssue") return setCreateIssueCanonicalId(paneRow.id);
        if (c === "dismiss") return runDismiss([paneRow.id]);
        if (c === "override") return setOverrideRow(paneRow);
        openDialog(c as TriageAction, [paneRow.id]);
      }}
    />
  ) : null;

  const cleanRerunCount = rows.filter(
    (r) => r.cleanRerun && ["new", "open", "in_progress"].includes(r.state),
  ).length;

  return (
    <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)]">
      <aside className="min-w-0">
        <QueueRail
          projectId={projectId}
          counts={counts}
          activeView={url.view}
          activeSavedId={url.savedViewId}
          currentQuery={query}
          canEditShared={canEdit}
        />
      </aside>

      <div className="min-w-0 space-y-4">
        <QueueFilters
          ref={searchRef}
          projectId={projectId}
          query={query}
          onChange={(next) =>
            setParams(queueUrlParams({ ...url, query: next, focusId: null }), {
              replace: true,
            })
          }
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">
            {queue.isLoading && knownViewCount === undefined ? (
              <span>Loading findings in {viewLabel}…</span>
            ) : (
              <>
                <output className="tabular-nums">
                  {plural(total, "finding")}
                </output>{" "}
                in {viewLabel}
              </>
            )}
          </p>
          <div className="flex items-center gap-2">
            {rows.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setZenIndex(0);
                  setZenOpen(true);
                }}
                className="h-8 gap-1.5 text-xs font-semibold shadow-2xs"
                title="Inspect queue findings in fullscreen Zen mode"
              >
                <Maximize2 className="size-3.5 text-primary" />
                <span>Zen Review ({rows.length})</span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setHelpOpen(true)}
              aria-keyshortcuts="?"
              className="hidden sm:inline-flex"
            >
              Keyboard shortcuts
            </Button>
          </div>
        </div>

        {cleanRerunCount > 0 && canEdit && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-foreground">
            <div className="flex items-center gap-2.5">
              <CheckCircle2 className="size-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <div>
                <span className="font-semibold text-emerald-950 dark:text-emerald-200">
                  {cleanRerunCount}{" "}
                  {cleanRerunCount === 1 ? "finding was" : "findings were"} not
                  observed in recent clean re-runs.
                </span>
                <span className="text-muted-foreground ml-1.5 hidden sm:inline">
                  Their scenarios passed with no problems.
                </span>
              </div>
            </div>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0 gap-1.5"
              disabled={recheckMutation.isPending}
              onClick={() => {
                recheckMutation.mutate(
                  { projectId },
                  {
                    onSuccess: () => {
                      toast.success("Findings verified from clean re-runs");
                      utils.finding.queue.invalidate();
                      utils.finding.queueCounts.invalidate();
                    },
                    onError: (err) =>
                      toastTrpcError(
                        err,
                        "Failed to verify findings from clean re-runs",
                      ),
                  },
                );
              }}
            >
              <CheckCircle2 className="size-4" />
              {recheckMutation.isPending ? "Verifying…" : "Verify from re-runs"}
            </Button>
          </div>
        )}

        <div
          ref={listRef}
          className="space-y-4"
          onFocus={(e) => {
            const key = (e.target as HTMLElement)
              .closest("[data-key]")
              ?.getAttribute("data-key");
            if (key && key !== focusedId) setFocusedId(key);
          }}
        >
          {list}
        </div>

        {queue.hasNextPage && (
          <Button
            variant="outline"
            onClick={() => queue.fetchNextPage()}
            disabled={queue.isFetchingNextPage}
          >
            Show more ({rows.length} of {total})
          </Button>
        )}
      </div>

      {paneRow && (
        <Sheet open onOpenChange={(o) => !o && openPane(null)}>
          <SheetContent
            side="right"
            className="w-full overflow-y-auto p-6 sm:max-w-xl lg:max-w-2xl"
            showCloseButton={false}
          >
            <SheetTitle className="sr-only">Finding detail</SheetTitle>
            <QueueDetailPane
              projectId={projectId}
              row={paneRow}
              actions={paneActions}
              onClose={() => openPane(null)}
              exportConfig={
                projectContext?.exportConfig
                  ? projectExportConfigSchema.parse(projectContext.exportConfig)
                  : undefined
              }
              hasLinkedRepo={!!projectData?.githubRepositoryId}
              onOpenZen={() => {
                const idx = rows.findIndex((r) => r.id === paneRow.id);
                setZenIndex(Math.max(0, idx));
                setZenOpen(true);
              }}
            />
          </SheetContent>
        </Sheet>
      )}

      {/* Fullscreen Zen Mode Carousel for the Queue */}
      <ZenFindingsCarousel
        findings={zenFindings}
        initialIndex={zenIndex}
        open={zenOpen}
        onOpenChange={setZenOpen}
        projectId={projectId}
        canEdit={canEdit}
        hasLinkedRepo={!!projectData?.githubRepositoryId}
        isTrackerConfigured={Boolean(projectData?.githubRepositoryId)}
        activeProvider={projectData?.githubRepositoryId ? "github" : null}
        onCreateIssue={(finding) => {
          setCreateIssueCanonicalId(finding.canonicalFindingId ?? finding.id);
        }}
        onLinkExisting={(canonId) => {
          setLinkExistingCanonicalId(canonId);
        }}
      />

      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>

      {canEdit && (
        <BatchActionBar
          count={selectedCount}
          noun="finding"
          onClear={() => {
            setSelected(new Set());
            setAllMatching(false);
          }}
        >
          {!allMatching && total > selected.size && total <= BULK_CAP && (
            <BatchActionButton onClick={() => setAllMatching(true)}>
              Select all matching ({total})
            </BatchActionButton>
          )}
          {url.view === "triage" && (
            <BatchActionButton
              onClick={() => runCommand("accept")}
              disabled={transition.isPending}
            >
              Accept
            </BatchActionButton>
          )}
          {url.view === "closed" ? (
            <BatchActionButton
              onClick={() => runMove("open", "Reopened")}
              disabled={transition.isPending}
            >
              Reopen
            </BatchActionButton>
          ) : (
            <BatchActionButton
              onClick={() => runCommand("dismiss")}
              disabled={transition.isPending}
            >
              Dismiss
            </BatchActionButton>
          )}
          {!allMatching && (
            <BatchExportButton
              selectedIds={selectionIds}
              projectId={projectId}
              exportConfig={
                projectContext?.exportConfig
                  ? projectExportConfigSchema.parse(projectContext.exportConfig)
                  : undefined
              }
              hasLinkedRepo={!!projectData?.githubRepositoryId}
            />
          )}
          {canEdit && (
            <BatchActionButton onClick={() => setBulkIssues(true)}>
              {allMatching ? "Create top issues…" : "Create issues…"}
            </BatchActionButton>
          )}
          {canEdit && !allMatching && (
            <BatchActionButton
              onClick={() => requests.queueBulk(selectionIds, "fix")}
              disabled={requests.isPending}
            >
              Queue fixes for agent
            </BatchActionButton>
          )}
        </BatchActionBar>
      )}
      {createIssueCanonicalId && (
        <CreateIssueDialog
          canonicalId={createIssueCanonicalId}
          open={!!createIssueCanonicalId}
          onOpenChange={(open) => !open && setCreateIssueCanonicalId(null)}
        />
      )}
      {linkExistingCanonicalId && (
        <LinkExistingDialog
          canonicalId={linkExistingCanonicalId}
          open={!!linkExistingCanonicalId}
          onOpenChange={(open) => !open && setLinkExistingCanonicalId(null)}
        />
      )}
      {bulkIssues && (
        <BulkCreateIssuesDialog
          projectId={projectId}
          canonicalIds={allMatching ? undefined : selectionIds}
          query={allMatching ? query : undefined}
          open={bulkIssues}
          onOpenChange={setBulkIssues}
        />
      )}

      {dialog && (
        <TriageActionDialog
          action={dialog.action}
          ids={dialog.ids}
          query={dialog.query}
          projectId={projectId}
          subject={dialog.subject}
          onClose={() => setDialog(null)}
          onDone={(message, changed) =>
            afterMove(dialog.query ? ids : dialog.ids, message, changed)
          }
        />
      )}
      {overrideRow && (
        <OverridePriorityDialog
          canonicalId={overrideRow.id}
          projectId={projectId}
          subject={overrideRow.title ?? "This finding"}
          currentOverride={overrideRow.priorityOverride}
          onClose={() => setOverrideRow(null)}
          onDone={setAnnouncement}
        />
      )}
      {helpOpen && <KeyboardHelpDialog onClose={() => setHelpOpen(false)} />}
      <SupersedeRequestDialog requests={requests} />
    </div>
  );
}

/** Row metadata after the built-ins: judge citation, wake condition, reason. */
function RowMeta({ row }: { row: QueueRowData }) {
  return (
    <>
      {row.cleanRerun && ["new", "open", "in_progress"].includes(row.state) && (
        <span className="inline-flex items-center gap-1 font-medium text-emerald-600 dark:text-emerald-400">
          <CheckCircle2 className="size-3.5" aria-hidden="true" />
          Clean in latest re-run
        </span>
      )}
      {(row.judgeRating === "no" || row.judgeRating === "partially") && (
        <span className="font-medium text-foreground">
          Judge: {row.judgeRating}
        </span>
      )}
      {row.state === "snoozed" && (
        <span>
          {row.snoozeUntil
            ? `Until ${new Date(row.snoozeUntil).toLocaleDateString()}`
            : row.snoozeTrigger === "worse"
              ? "Until it gets worse"
              : "Until the next run"}
        </span>
      )}
      {row.state === "dismissed" &&
        (row.judgeRating === "no" || row.judgeRating === "partially") && (
          <span className="font-medium text-mark-critical">
            Dismissed but cited by judge
          </span>
        )}
    </>
  );
}

function groupLabel(group: string | undefined, g: QueueGroupData): string {
  if (!g.label) {
    return group === "page" ? "No page recorded" : "Not set";
  }
  return g.label;
}

function RerunCommand({ scenarioId }: { scenarioId: string }) {
  const command = `/iamu:run ${scenarioId}`;
  return (
    <div className="flex min-w-0 items-center gap-2">
      <code className="truncate rounded-sm bg-muted px-1.5 py-0.5 font-mono text-xs">
        {command}
      </code>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          navigator.clipboard
            ?.writeText(command)
            .then(() => toast.success("Re-run command copied"))
            .catch(() => toast.error("Couldn't copy"));
        }}
      >
        <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
        Copy re-run command
      </Button>
    </div>
  );
}

function QueueEmpty({
  projectId,
  view,
  counts,
  filtered,
  scoped,
}: {
  projectId: string;
  view: QueueSystemView | null;
  counts: Partial<Record<QueueSystemView, number>> | undefined;
  filtered: boolean;
  /** The page scope narrows runs (a period or a build). */
  scoped: boolean;
}) {
  const base = `/projects/${projectId}/findings`;
  const firstRunCopy = useFirstRunCopy();
  const any = counts ? Object.values(counts).some((n) => (n ?? 0) > 0) : false;
  if (scoped && !any) return <ScopeEmptyState what="findings" />;
  if (!any && !filtered) {
    return (
      <EmptyState
        icon={Route}
        title="Findings appear after your first walkthrough"
        description={firstRunCopy}
        variant="compact"
        actions={
          <Button asChild size="sm">
            <Link to={`/projects/${projectId}/scenarios`}>Go to Scenarios</Link>
          </Button>
        }
      />
    );
  }
  if (filtered) {
    return (
      <EmptyState
        icon={Inbox}
        title="No findings match these filters"
        variant="compact"
      />
    );
  }
  if (view === "triage") {
    return (
      <EmptyState
        icon={Inbox}
        title="Inbox zero"
        description="New findings from the next run land here."
        variant="compact"
        actions={
          <Button asChild size="sm">
            <Link to={`${base}?view=fix_next`}>Go to Ready to fix</Link>
          </Button>
        }
      />
    );
  }
  if (view === "fix_next") {
    const waiting = counts?.awaiting_rerun ?? 0;
    const triage = counts?.triage ?? 0;
    if (triage > 0) {
      return (
        <EmptyState
          icon={Inbox}
          title="No triaged problems ready to fix"
          description={`${plural(triage, "finding")} in Unreviewed must be reviewed before appearing here.`}
          variant="compact"
          actions={
            <Button asChild size="sm">
              <Link to={`${base}?view=triage`}>Go to Unreviewed</Link>
            </Button>
          }
        />
      );
    }
    return (
      <EmptyState
        icon={Inbox}
        title="No open problems"
        description={
          waiting > 0
            ? `${plural(waiting, "fix")} ${waiting === 1 ? "is" : "are"} waiting for a re-run.`
            : "Nothing to fix right now."
        }
        variant="compact"
        actions={
          waiting > 0 ? (
            <Button asChild size="sm">
              <Link to={`${base}?view=awaiting_rerun`}>
                Go to Awaiting re-run
              </Link>
            </Button>
          ) : undefined
        }
      />
    );
  }
  return <EmptyState icon={Inbox} title="Nothing here" variant="compact" />;
}
